import React, { Component } from 'react'
import { Header, Icon, Divider, Dropdown, Form, Label, Button, Modal, Table } from 'semantic-ui-react'
import moment from 'moment/moment'
import matchSorter from 'match-sorter'
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import { getLicenseName } from '../../common/licenseHelper'
import GeneratedLicenseAdd from '../GeneratedLicenses/GeneratedLicenseAdd'

// Extract the computer name from a machine id by stripping the trailing hardware
// fingerprint, e.g. "QC-RACK3-RIGSIM-71CB-B83E-623F-9D49-D3BF" -> "QC-RACK3-RIGSIM".
// The fingerprint is a run of trailing "-XXXX" groups (4 hex chars each).
const computerName = (machineId) =>
    String(machineId || '').replace(/(-[0-9A-Fa-f]{4})+$/, '')

// case-insensitive contains filter for a text column
const textFilter = (key) => ({
    filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: [key] }),
    filterAll: true
})

const columns = [
    { Header: 'Full name', accessor: 'fullname', ...textFilter('fullname') },
    { Header: 'Email', accessor: 'email', ...textFilter('email') },
    { Header: 'Company', accessor: 'company', ...textFilter('company') },
    { Header: 'License id', accessor: 'license_name', ...textFilter('license_name') },
    { Header: 'Product', id: 'license_type', accessor: d => getLicenseName(d.license_type), width: 140 },
    { Header: 'Machine id', accessor: 'machine_id', ...textFilter('machine_id') },
    {
        Header: 'Create date', id: 'create_date',
        accessor: (row) => moment(row.create_date, 'DD.MM.YYYY HH:mm:ss').format('YYYY.MM.DD HH:mm:ss').toString(),
        width: 160
    },
    {
        Header: 'Expire date', id: 'expire_date',
        accessor: (row) => moment(row.expire_date, 'DD.MM.YYYY').format('YYYY.MM.DD').toString(),
        width: 110
    },
    { Header: 'Origin', accessor: 'origin', width: 90 }
]

// Report: pick a company and/or a machine id and see every license generated on it.
// Built from the generated-licenses data (/view-all-license-requests/), which
// already carries machine_id per row -- no extra backend call required.
export default class MachineReport extends Component {
    constructor(props) {
        super(props)
        this.state = { company: null, email: null, machine: null, open: false, computerModalOpen: false }
    }

    componentDidMount() {
        if (this.props.getAllLicenses) this.props.getAllLicenses()
    }

    uniqueOptions = (rows, key) =>
        [...new Set(rows.map(r => r[key]).filter(Boolean))]
            .sort()
            .map(v => ({ key: v, text: v, value: v }))

    // Open the wizard empty (top button)
    show = () => {
        if (this.props.initLicenseWizard) this.props.initLicenseWizard()
        this.setState({ open: true })
    }

    // Open the wizard pre-filled from a report row (fetches the license request first)
    startFromRow = (row) => {
        Promise.resolve(this.props.startLicenseFromRow(row)).then(() => this.setState({ open: true }))
    }

    // Refresh the report data when the wizard closes so a new license shows up
    close = () => {
        this.setState({ open: false })
        if (this.props.getAllLicenses) this.props.getAllLicenses()
    }

    render() {
        const data = this.props.data || []
        const { isAdmin } = this.props
        const { company, email, machine, open } = this.state

        // Cascade: Company -> Email -> Machine id
        const byCompany = company ? data.filter(r => r.company === company) : data
        const byEmail = email ? byCompany.filter(r => r.email === email) : byCompany
        const companyOptions = this.uniqueOptions(data, 'company')
        const emailOptions = this.uniqueOptions(byCompany, 'email')
        const machineOptions = this.uniqueOptions(byEmail, 'machine_id')
        const rows = machine ? byEmail.filter(r => r.machine_id === machine) : byEmail
        const show = !!(company || email || machine)
        // Unique machine ids (distinct hardware) and unique computer names
        // (machine id with the fingerprint stripped) within the current selection.
        const uniqueMachines = new Set(rows.map(r => r.machine_id).filter(Boolean)).size
        // First matching row per unique computer name (in current selection).
        const firstByComputer = []
        const seenComputers = new Set()
        rows.forEach(r => {
            const c = computerName(r.machine_id)
            if (!c || seenComputers.has(c)) return
            seenComputers.add(c)
            firstByComputer.push(r)
        })
        const uniqueComputers = firstByComputer.length

        // Per-row action: open the Add-license wizard pre-filled from that license.
        const allColumns = columns.concat([{
            Header: 'Action',
            width: 130,
            filterable: false,
            sortable: false,
            Cell: ({ original }) => (
                <Button size='mini' compact icon='plus' content='New license'
                        disabled={!isAdmin} onClick={() => this.startFromRow(original)}/>
            )
        }])

        return (
            <React.Fragment>
                <Header as='h2'>
                    <Icon name='desktop'/>
                    <Header.Content>Licenses per machine</Header.Content>
                </Header>

                <Button compact disabled={!isAdmin} onClick={this.show} style={{ marginBottom: '1em' }}>
                    <Icon name='key'/> Add new license
                </Button>

                <Form>
                    <Form.Group widths='equal'>
                        <Form.Field>
                            <label>Company</label>
                            <Dropdown placeholder='All companies' fluid search selection clearable
                                      options={companyOptions} value={company}
                                      onChange={(e, { value }) => this.setState({ company: value || null, email: null, machine: null })}/>
                        </Form.Field>
                        <Form.Field>
                            <label>Email</label>
                            <Dropdown placeholder='All emails' fluid search selection clearable
                                      options={emailOptions} value={email}
                                      onChange={(e, { value }) => this.setState({ email: value || null, machine: null })}/>
                        </Form.Field>
                        <Form.Field>
                            <label>Machine id</label>
                            <Dropdown placeholder='Select machine id' fluid search selection clearable
                                      options={machineOptions} value={machine}
                                      onChange={(e, { value }) => this.setState({ machine: value || null })}/>
                        </Form.Field>
                    </Form.Group>
                </Form>

                <Divider hidden/>

                {show
                    ? <React.Fragment>
                        <Label basic>
                            {rows.length} license{rows.length === 1 ? '' : 's'}
                            {machine ? <span> on machine <b>{machine}</b></span> : null}
                            {email ? <span> for <b>{email}</b></span> : null}
                            {company ? <span> in <b>{company}</b></span> : null}
                        </Label>
                        <Label basic>
                            <Icon name='desktop'/>{uniqueMachines} unique machine id{uniqueMachines === 1 ? '' : 's'}
                        </Label>
                        <Label basic as='a' onClick={() => this.setState({ computerModalOpen: true })}
                               title='Show one license per unique computer'>
                            <Icon name='computer'/>{uniqueComputers} unique computer{uniqueComputers === 1 ? '' : 's'}
                        </Label>
                        <Divider hidden/>
                        <ReactTable
                            filterable
                            data={rows}
                            columns={allColumns}
                            defaultSorted={[{ id: 'create_date', desc: true }]}
                            defaultPageSize={20}
                            className="-striped -highlight"/>
                    </React.Fragment>
                    : <p>Select a company and/or a machine id to see the licenses generated on it.</p>}

                <GeneratedLicenseAdd open={open} onClose={this.close}/>

                <Modal size='large' dimmer='inverted' open={this.state.computerModalOpen}
                       onClose={() => this.setState({ computerModalOpen: false })}>
                    <Modal.Header>
                        <Icon name='computer'/> {uniqueComputers} unique computer{uniqueComputers === 1 ? '' : 's'}
                    </Modal.Header>
                    <Modal.Content scrolling>
                        <Table compact celled>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Computer</Table.HeaderCell>
                                    <Table.HeaderCell>Machine id</Table.HeaderCell>
                                    <Table.HeaderCell>License id</Table.HeaderCell>
                                    <Table.HeaderCell>Expire date</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {firstByComputer.map(r => (
                                    <Table.Row key={computerName(r.machine_id)}>
                                        <Table.Cell><b>{computerName(r.machine_id)}</b></Table.Cell>
                                        <Table.Cell>{r.machine_id}</Table.Cell>
                                        <Table.Cell>{r.license_name}</Table.Cell>
                                        <Table.Cell>{moment(r.expire_date, 'DD.MM.YYYY').format('YYYY.MM.DD')}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={() => this.setState({ computerModalOpen: false })}>Close</Button>
                    </Modal.Actions>
                </Modal>
            </React.Fragment>
        )
    }
}
